
import { useQuery } from '@tanstack/react-query'
import cx from 'classnames'
import { useCallback, useEffect, useState } from 'react'
import { LuGripHorizontal, LuGripVertical } from 'react-icons/lu'
import { MdAdd, MdClose, MdFullscreen, MdRemove } from 'react-icons/md'
import { useParams } from 'react-router-dom'
import { useMeasure } from 'react-use'
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch'

import http from '../../../lib/http'
import * as passive from '../../../lib/passive-events'
import { useAppSelector } from '../../../store'

import { BehavioralWidget } from './behavioral-widget'
import { DocumentsGrid } from './components/documents-grid'
import { ErrorPlaceHolder } from './components/error-placeholder'
import { useResizeableColumnWidth } from './components/resize'
import { BehavioralContextProvider, useBehavioralContext } from './context'

let resizeableColumnWidthLocalStorageKey = 'behavioral-page:resizeable-column:width'
let resizeableColumnHeightLocalStorageKey = 'behavioral-page:resizeable-column:height'

export function Behavioral (): JSX.Element {
  const params = useParams()
  const permission = useAppSelector(store => store?.globalVars?.permissions?.behavioral)

  const patientId = params.id
  const clientName = params.clientName

  if (!patientId || !clientName) {
    return (
      <ErrorPlaceHolder
        title='Patient ID or Client Name are incorrect'
      />
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <BehavioralContextProvider>
        <DocumentsGrid
          patientId={patientId}
          clientName={clientName}
        />
        <BehavioralWidgetWithDocumentViewer
          readOnly={permission !== 'write'}
          patientId={patientId}
          clientName={clientName}
        />

      </BehavioralContextProvider>
    </div>
  )
}

interface Props {
  readOnly: boolean
  patientId: string
  clientName: string
}

function BehavioralWidgetWithDocumentViewer ({ readOnly, patientId, clientName }: Props): JSX.Element {
  const behavioralContext = useBehavioralContext()

  const [ measureRef, { width: windowWidth }] = useMeasure<HTMLDivElement>()

  if (!patientId) {
    return (
      <ErrorPlaceHolder
        title='Patient ID is incorrect'
      />
    )
  }

  return (
    <div
      ref={measureRef}
      className={cx('flex rounded-lg relative max-w-full',
        {
          'flex-row mt-6': behavioralContext.document?.position === 'split',
          'flex-col gap-4': behavioralContext.document?.position === 'bottom'
        })}
    >
      {behavioralContext?.document?.position === 'bottom' && (
        <BottomView
          clientName={clientName}
          patientId={patientId}
        />
      )}
      <BehavioralWidget
        patientId={patientId}
        readOnly={readOnly}
      />
      {behavioralContext?.document?.position === 'split' && (
        <SplitView
          maxWidth={windowWidth * 0.6}
          patientId={patientId}
          clientName={clientName}
        />
      )}

    </div>
  )
}

interface SplitViewProps {
  maxWidth: number
  patientId: string
  clientName: string
}

function SplitView ({ maxWidth, clientName, patientId }: SplitViewProps): JSX.Element | null {
  const behavioralContext = useBehavioralContext()

  let [ width, setWidth ] = useState(useResizeableColumnWidth({ userId: patientId, defaultWidth: 300, key: resizeableColumnWidthLocalStorageKey }))
  let [ isDragging, setIsDragging ] = useState(false)

  let handleMouseDown = useCallback(() => {
    setIsDragging(true)
  }, [])

  let handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  let handleMouseMove = useCallback((event: MouseEvent) => {
    if (!isDragging) {
      return
    }

    setWidth(prev => Math.min(maxWidth, prev - event.movementX))
  }, [ isDragging, maxWidth ])

  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp, passive.options)
    window.addEventListener('mousemove', handleMouseMove, passive.options)

    return () => {
      window.removeEventListener('mouseup', handleMouseUp, passive.options)
      window.removeEventListener('mousemove', handleMouseMove, passive.options)
    }
  }, [ handleMouseUp, handleMouseMove ])

  useEffect(() => {
    if (!isDragging) {
      return
    }

    let previousValue = document.body.style.cursor
    document.body.style.cursor = 'grabbing'

    return () => {
      document.body.style.cursor = previousValue
    }
  }, [ isDragging ])

  useEffect(() => {
    localStorage.setItem(resizeableColumnWidthLocalStorageKey, width.toString())
  }, [ width ])

  let query = useQuery({
    queryKey: [
      'preview',
      behavioralContext?.document?.id ?? null
    ],

    async queryFn ({ queryKey }) {
      let [ , id ] = queryKey

      if (!id || !patientId || !behavioralContext.document?.documentId) {
        return
      }

      let response = await http.getRequest(`/behavioral/${patientId}/documents/${id}/download`, {
        clientName,
        documentId: behavioralContext.document?.documentId
      })

      return response.data
    },

    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchInterval: false
  })

  if (!behavioralContext.document) {
    return null
  }

  return (
    <div className='relative z-[1000]'>

      <CloseViewerButton onClick={() => behavioralContext.setDocument(null)} />

      <div
        className='relative flex flex-row bg-[#525659]'
        style={{
          width: `${width}px`,
          height: '75vh'
        }}
      >
        {query.data && behavioralContext?.document?.mimeType === 'application/pdf' && (
          <embed
            src={`data:${behavioralContext.document.mimeType};base64,${query?.data as string ?? ''}`}
            type={behavioralContext.document.mimeType}
            className={cx('w-full flex-grow select-none flex-1 ', [ isDragging && 'pointer-events-none' ])}
          />
        )}

        {query.data && behavioralContext?.document?.mimeType !== 'application/pdf' && (
          <ImageViewer
            alt='document'
            src={`data:${behavioralContext.document.mimeType};base64,${query?.data as string ?? ''}`}
            className={cx('select-none max-w-full pointer-events-none flex-1')}
          />
        )}
        <button
          className='absolute z-10 bg-gray-200 bottom-0 hover:bg-gray-300 left-0 cursor-grab -translate-x-full min-w-[14px] h-full'
          data-test-id='grip-bar-split-view'
          onMouseDown={handleMouseDown}
        >
          <LuGripVertical />
        </button>
      </div>
    </div>
  )
}

interface BottomViewProps {
  patientId: string
  clientName: string
}

function BottomView ({ clientName, patientId }: BottomViewProps): JSX.Element | null {
  const behavioralContext = useBehavioralContext()

  let [ height, setHeight ] = useState(useResizeableColumnWidth({
    userId: patientId,
    defaultWidth: 300,
    key: resizeableColumnHeightLocalStorageKey
  }))
  let [ isDragging, setIsDragging ] = useState(false)

  let handleMouseDown = useCallback(() => {
    setIsDragging(true)
  }, [])

  let handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  let handleMouseMove = useCallback((event: MouseEvent) => {
    if (!isDragging) {
      return
    }

    setHeight(prev => Math.min(800, prev + event.movementY))
  }, [ isDragging ])

  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp, passive.options)
    window.addEventListener('mousemove', handleMouseMove, passive.options)

    return () => {
      window.removeEventListener('mouseup', handleMouseUp, passive.options)
      window.removeEventListener('mousemove', handleMouseMove, passive.options)
    }
  }, [ handleMouseUp, handleMouseMove ])

  useEffect(() => {
    if (!isDragging) {
      return
    }

    let previousValue = document.body.style.cursor
    document.body.style.cursor = 'grabbing'

    return () => {
      document.body.style.cursor = previousValue
    }
  }, [ isDragging ])

  useEffect(() => {
    localStorage.setItem(resizeableColumnHeightLocalStorageKey, height.toString())
  }, [ height ])

  let query = useQuery({
    queryKey: [
      'preview',
      behavioralContext?.document?.id ?? null
    ],

    async queryFn ({ queryKey }) {
      let [ , id ] = queryKey

      if (!id || !patientId || !behavioralContext.document?.documentId) {
        return
      }

      let response = await http.getRequest(`/behavioral/${patientId}/documents/${id}/download`, {
        clientName,
        documentId: behavioralContext.document?.documentId
      })

      const img = new Image()

      img.onload = () => {
        setHeight(img.height)
      }

      img.src = response.data.data

      return response.data
    },

    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchInterval: false
  })

  if (!behavioralContext.document) {
    return null
  }

  return (
    <div className='relative mt-5'>
      <CloseViewerButton
        className='-top-8 right-0'
        onClick={() => behavioralContext.setDocument(null)}
      />
      <div
        className='relative flex flex-row bg-[#525659] rounded-2xl overflow-hidden'
        style={{
          width: '100%',
          height
        }}
      >
        {query.data && behavioralContext?.document?.mimeType === 'application/pdf' && (
          <embed
            src={`data:${behavioralContext.document.mimeType};base64,${query?.data as string ?? ''}`}
            type={behavioralContext.document.mimeType}
            className={cx('w-full flex-grow select-none flex-1', [ isDragging && 'pointer-events-none' ])}
          />
        )}

        {query.data && behavioralContext?.document?.mimeType !== 'application/pdf' && (
          <ImageViewer
            alt='document'
            src={`data:${behavioralContext.document.mimeType};base64,${query?.data as string ?? ''}`}
            className={cx('select-none fit-contain max-w-full pointer-events-none flex-1')}
          />
        )}
      </div>
      <button
        className={cx('absolute z-10 bg-gray-200 rounded-b-xl hover:bg-gray-300 transition-colors flex flex-row justify-center items-center left-0 cursor-grab py-1 bottom-0 w-full')}
        data-test-id='grip-bar-below-view'
        onMouseDown={handleMouseDown}
      >
        <LuGripHorizontal />
      </button>
    </div>
  )
}

type ImageViewerProps = React.ImgHTMLAttributes<HTMLImageElement>

const actionButtonClassName = 'flex cursor-pointer items-center justify-center text-[#f1f1f1] h-8 w-8 rounded-full hover:bg-white hover:bg-opacity-[8%]'

function ImageViewer ({ ...imageRest }: ImageViewerProps): JSX.Element {
  return (
    <TransformWrapper centerOnInit>
      {({ zoomIn, zoomOut, centerView, resetTransform, ...rest }) => {
        return (
          <div className='flex flex-col w-full'>
            <Controls
              onReset={resetTransform}
              onZoomIn={zoomIn}
              onZoomOut={zoomOut}
            />
            <TransformComponent
              wrapperClass='w-full h-full'
            >
              <img
                {...imageRest}
                alt={imageRest.alt}
              />
            </TransformComponent>
          </div>
        )
      }}
    </TransformWrapper>
  )
}

interface ControlsProps {
  onZoomIn: () => void
  onZoomOut: () => void
  onReset: () => void
}

function Controls ({ onZoomIn, onZoomOut, onReset }: ControlsProps): JSX.Element {
  return (
    <div className='bg-[#323639] h-[56px] w-full'>
      <div className='flex flex-row gap-4 px-8 h-full items-center justify-center'>
        <button
          className={actionButtonClassName}
          title='Zoom Out'
          onClick={() => onZoomOut()}
        >
          <MdRemove className='w-[20px] h-[20px]' />
        </button>
        <button
          className={actionButtonClassName}
          title='Zoom In'
          onClick={() => onZoomIn()}
        >
          <MdAdd className='w-[20px] h-[20px]' />
        </button>
        <button
          className={actionButtonClassName}
          title='Fit to page'
          onClick={() => {
            onReset()
          }}
        >
          <MdFullscreen className='w-[20px] h-[20px]' />
        </button>
      </div>
    </div>
  )
}

function CloseViewerButton ({ className, ...rest }: React.ComponentProps<'button'>): JSX.Element {
  return (
    <button
      data-test-id='close-viewer'
      title='Close'
      className={cx('absolute -top-[1.6rem] -right-2 flex flex-row justify-center items-center w-7 h-7 text-[#646464] rounded-full hover:text-[#464646] transition-colors z-10 cursor-pointer', className)}
      {...rest}
    >
      <MdClose
        size='1.3rem'
        className='text-inherit'
        // strokeWidth={3}
      />
    </button>
  )
}
